import { SaleModel } from 'api-hooks/sale/common';
import { SaleDetailModel } from 'api-hooks/sale/model';
import { useGetDetailSale, useGetRefundSales } from 'api-hooks/sale/query';
import { OnChangeParams, Option } from 'baseui/select';
import { QueryTransformer } from 'common/helpers/common';
import { capitalize, transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(item: SaleModel): Option {
  const isCorporate = ['pt', 'cv'].includes(item.customer?.title || '');
  const name =
    transformLabel(
      [
        isCorporate
          ? item?.customer?.title?.toUpperCase()
          : capitalize(item.customer.title),
        item.customer.name,
      ],
      ' ',
    ) + ` (${item.customer.phone})`;
  return {
    id: item.id,
    label: transformLabel([item.invoiceNumber, item.code, name], ' - '),
    extra: item,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value: SaleDetailModel) => void;
  noMargin?: boolean;
  disabled?: boolean;
}

export default function RefundSalesSelectInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:invoice_number'),
    placeholder = t('common:invoice_number'),
  } = props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetRefundSales,
    useDetailLazyQueryHook: useGetDetailSale,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          // filter: {
          //   transaction_type: SaleTransactionTypeEnum.Document,
          // } as unknown as string,
        },
      }),
      [],
    ),
    renderCreate: false,
    onClickCreate: React.useCallback(() => {
      props.onClose?.();
    }, [props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, SaleDetailModel);
        props.onAfterDetailChange?.(newResult.data);
        return transformer(newResult.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
    staleTime: undefined,
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      disabled={props.disabled}
      {...selectProps}
    />
  );
}
